import React, { useState, useEffect } from 'react';
import { auth } from '@/firebase/config';
import { reload, signOut } from 'firebase/auth';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { useAuth } from '@/contexts/AuthContext';
import { AlertCircle, Mail, CheckCircle, RefreshCw, LogOut } from 'lucide-react';
import { redirectToMainDomain, getLoginUrl } from '@/utils/domainUtils';

const EmailVerificationPage: React.FC = () => {
  const { user, updateUser, logout } = useAuth();
  const [isSending, setIsSending] = useState(false);
  const [sentSuccessfully, setSentSuccessfully] = useState(false);
  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isLoadingCheck, setIsLoadingCheck] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  // Check email verification status from Firebase Auth and Firestore
  const checkVerificationStatus = async () => {
    if (!auth.currentUser && !user) {
      setError('No user is currently signed in');
      return false;
    }
    
    setIsChecking(true);
    setError('');
    try {
      // First, check Firestore for invited users (they have verified_email: true in Firestore)
      if (user?.id) {
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('@/firebase/config');
        const userDoc = await getDoc(doc(db, 'users', user.id));
        
        if (userDoc.exists()) {
          const userData = userDoc.data();
          // If Firestore has verified_email flag (for invited users), use it
          if (userData.verified_email === true) {
            console.log('✅ User is verified via invitation (from Firestore)');
            setIsVerified(true);
            
            // Update user in context and localStorage
            updateUser({ verified_email: true });
            
            // Redirect to home
            setTimeout(() => {
              redirectToMainDomain('/home');
            }, 500);
            return true;
          }
        }
      }
      
      // For non-invited users, check Firebase Auth emailVerified status
      if (auth.currentUser) {
        // Reload user to get latest emailVerified status
        await reload(auth.currentUser);
        const verified = auth.currentUser.emailVerified;
        
        console.log('🔍 Email verification status from Firebase Auth:', verified);
        
        if (verified) {
          setIsVerified(true);
          
          // Update user in context and localStorage to reflect verification
          if (user) {
            updateUser({ verified_email: true });
            console.log('✅ Updated user verification status');
          }
          
          // Show success message briefly, then redirect
          setTimeout(() => {
            console.log('🔄 Redirecting to dashboard...');
            redirectToMainDomain('/home');
          }, 1500);
          return true;
        } else {
          setError('Email is not yet verified. Please click the verification link in your email.');
        }
        
        return verified;
      }
      
      return false;
    } catch (error: any) {
      console.error('Error checking verification status:', error);
      setError('Failed to check verification status. Please try again.');
      return false;
    } finally {
      setIsChecking(false);
    }
  };

  // Check verification status on mount - check Firestore first for invited users
  useEffect(() => {
    const initialCheck = async () => {
      setIsLoadingCheck(true);
      
      // If user is already marked as verified in context, redirect immediately
      if (user?.verified_email) {
        redirectToMainDomain('/home');
        return;
      }
      
      // If user exists but not verified in context, check Firestore for invited users
      if (user?.id) {
        try {
          const { doc, getDoc } = await import('firebase/firestore');
          const { db } = await import('@/firebase/config');
          const userDoc = await getDoc(doc(db, 'users', user.id));
          
          if (userDoc.exists()) {
            const userData = userDoc.data();
            // If Firestore has verified_email flag (for invited users), update and redirect
            if (userData.verified_email === true) {
              console.log('✅ User is verified via invitation (from Firestore)');
              updateUser({ verified_email: true });
              redirectToMainDomain('/home');
              return;
            }
          }
        } catch (error) {
          console.error('Error checking Firestore for verification:', error);
        }
      }
      
      setIsLoadingCheck(false);
      
      // If user exists but not verified, check full verification status
      if (user && !user.verified_email) {
        checkVerificationStatus();
      } else if (!auth.currentUser && !user) {
        // No user in context or auth - might be loading, wait a bit
        setTimeout(() => {
          if (!auth.currentUser && !user) {
            setError('No user is currently signed in. Please log in first.');
          }
        }, 2000);
      }
    };
    
    initialCheck();
  }, [user]);

  const handleResendVerification = async () => {
    if (!auth.currentUser) {
      setError('No user is currently signed in');
      return;
    }

    setIsSending(true);
    setError('');
    setSentSuccessfully(false);

    try {
      const functions = getFunctions();
      const sendCustomEmailVerification = httpsCallable(functions, 'sendCustomEmailVerification');
      await sendCustomEmailVerification({
        userId: auth.currentUser.uid,
        email: auth.currentUser.email,
        displayName: auth.currentUser.displayName || 'User'
      });
      setSentSuccessfully(true);
      console.log('✅ Verification email sent successfully');
      
      // Clear success message after 5 seconds
      setTimeout(() => {
        setSentSuccessfully(false);
      }, 5000);
    } catch (error: any) {
      console.error('Error sending verification email:', error);
      
      if (error.code === 'auth/too-many-requests' || error.message?.includes('too-many-requests')) {
        setError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setError(error.message || 'Failed to send verification email. Please try again.');
      }
    } finally {
      setIsSending(false);
    }
  };

  const handleCheckAgain = async () => {
    await checkVerificationStatus();
  };

  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      // Sign out from Firebase Auth
      if (auth.currentUser) {
        await signOut(auth);
      }
      
      // Call AuthContext logout to clear local state
      logout();
      
      // Redirect to login page
      window.location.href = getLoginUrl();
    } catch (error: any) {
      console.error('Error during logout:', error);
      setError('Failed to logout. Please try again.');
      setIsLoggingOut(false);
    }
  };

  // If user is already verified, show success message
  if (isVerified || (user && user.verified_email)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-green-100 mb-4">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <h2 className="text-3xl font-bold text-gray-900">Email Verified!</h2>
            <p className="mt-2 text-gray-600">Your email has been successfully verified. Redirecting...</p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoadingCheck) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Checking verification status...</p>
        </div>
      </div>
    );
  }

  if (!user && !auth.currentUser) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-start mb-4">
              <AlertCircle className="h-5 w-5 text-red-600 mr-3 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  No user signed in
                </h3>
                <p className="text-sm text-gray-600">
                  Please log in first to verify your email.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center relative">
          {/* Logout button positioned at top right */}
          <button
            onClick={handleLogout}
            disabled={isLoggingOut}
            className="absolute top-0 right-0 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Logout and sign in as different user"
          >
            {isLoggingOut ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600 mr-2"></div>
                Logging out...
              </>
            ) : (
              <>
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </>
            )}
          </button>
          
          <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-yellow-100 mb-4">
            <Mail className="h-6 w-6 text-yellow-600" />
          </div>
          <h2 className="text-3xl font-bold text-gray-900">Verify Your Email</h2>
          <p className="mt-2 text-gray-600">
            We need to verify your email address before you can access the dashboard.
          </p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-start mb-4">
            <AlertCircle className="h-5 w-5 text-yellow-600 mr-3 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-medium text-gray-900 mb-2">
                Check your inbox
              </h3>
              <p className="text-sm text-gray-600">
                We've sent a verification email to <strong>{user?.email || auth.currentUser?.email || 'your email'}</strong>.
                Please check your inbox and click the verification link to activate your account.
              </p>
              <p className="mt-2 text-xs text-gray-500">
                If you don't see the email, check your spam folder.
              </p>
            </div>
          </div>

          {sentSuccessfully && (
            <div className="mb-4 flex items-center text-sm text-green-700 bg-green-50 rounded-md p-3">
              <CheckCircle className="h-4 w-4 mr-2" />
              Verification email sent! Please check your inbox.
            </div>
          )}

          {error && (
            <div className="mb-4 flex items-center text-sm text-red-700 bg-red-50 rounded-md p-3">
              <AlertCircle className="h-4 w-4 mr-2" />
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleResendVerification}
              disabled={isSending}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSending ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Sending...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Resend Verification Email
                </>
              )}
            </button>
            
            <button
              onClick={handleCheckAgain}
              disabled={isChecking}
              className="w-full inline-flex justify-center items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isChecking ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mr-2"></div>
                  Checking...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  I've Verified My Email
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
